import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Plus, Pencil, Trash2, CalendarRange } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  message: '',
  start_date: todayIso(),
  end_date: '',
  start_time: '00:00',
  end_time: '23:59',
  is_active: true,
  severity: 'info',
});

// Severity → small label + pill colour used in the list. Keeps the JSX
// declarative and matches the HomeBanner palette.
const SEVERITY_PILL = {
  info: { label: 'Info', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  warning: { label: 'Warning', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  alert: { label: 'Alert', cls: 'bg-red-100 text-red-800 border-red-300' },
};

/**
 * BannerManager — Admin UI to publish / edit / delete the floating banners
 * that appear on every employee's Home page between ``start_date`` and
 * ``end_date``. Driven by ``/api/banners`` (admin) + ``/api/banners/active``
 * (read).
 *
 * Lists ALL banners (active + expired + future) latest-first, with a small
 * coloured "status pill" so the admin can see at a glance which one is
 * actually live. Delete uses a Shadcn AlertDialog (the platform suppresses
 * window.confirm in preview/prod).
 */
const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/banners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load banners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      message: b.message,
      start_date: b.start_date,
      end_date: b.end_date,
      start_time: b.start_time || '00:00',
      end_time: b.end_time || '23:59',
      is_active: !!b.is_active,
      severity: b.severity || 'info',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.message.trim()) { toast.error('Message is required'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Both dates are required'); return; }
    if (!form.start_time || !form.end_time) { toast.error('Both start and end times are required'); return; }
    // Same lexicographic check the backend uses — gives instant feedback so
    // the admin doesn't have to wait for a round-trip to discover an invalid
    // window.
    const startCombined = `${form.start_date}T${form.start_time}`;
    const endCombined = `${form.end_date}T${form.end_time}`;
    if (endCombined < startCombined) {
      toast.error('End (date + time) cannot be before start (date + time)');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editing) {
        await axios.put(`${API}/banners/${editing.id}`, form, config);
        toast.success('Banner updated');
      } else {
        await axios.post(`${API}/banners`, form, config);
        toast.success('Banner published');
      }
      setDialogOpen(false);
      await fetchBanners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/banners/${toDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Banner deleted');
      setToDelete(null);
      await fetchBanners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete banner');
    } finally {
      setDeleting(false);
    }
  };

  const statusOf = (b) => {
    const today = todayIso();
    if (!b.is_active) return { label: 'Inactive', cls: 'bg-gray-100 text-gray-700 border-gray-300' };
    if (today < b.start_date) return { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700 border-blue-300' };
    if (today > b.end_date) return { label: 'Expired', cls: 'bg-gray-100 text-gray-500 border-gray-300' };
    return { label: 'Live', cls: 'bg-green-100 text-green-700 border-green-300' };
  };

  return (
    <Card data-testid="banner-manager">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-amber-600" />
          <CardTitle>Home Page Banners</CardTitle>
        </div>
        <Button onClick={openCreate} data-testid="banner-new-btn" className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-1" /> Publish New Banner
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-500 mb-4">
          Banners show on every employee&apos;s Home page between their start and end dates.
          Set <span className="font-medium">Active</span> off to temporarily hide one without deleting it.
        </p>

        {loading ? (
          <div className="text-center py-8 text-gray-500" data-testid="banner-loading">Loading…</div>
        ) : banners.length === 0 ? (
          <div className="text-center py-10 text-gray-500 border-2 border-dashed rounded-lg" data-testid="banner-empty">
            No banners yet. Click <span className="font-medium">&quot;Publish New Banner&quot;</span> to create one.
          </div>
        ) : (
          <div className="space-y-3" data-testid="banner-list">
            {banners.map((b) => {
              const s = statusOf(b);
              return (
                <div
                  key={b.id}
                  data-testid={`banner-row-${b.id}`}
                  className="flex items-start justify-between gap-4 p-4 border rounded-lg hover:bg-gray-50 transition"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${s.cls}`} data-testid={`banner-status-${b.id}`}>
                        {s.label}
                      </span>
                      {(() => {
                        const sev = SEVERITY_PILL[b.severity] || SEVERITY_PILL.info;
                        return (
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full border ${sev.cls}`}
                            data-testid={`banner-severity-${b.id}`}
                          >
                            {sev.label}
                          </span>
                        );
                      })()}
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <CalendarRange className="w-3 h-3" />
                        {b.start_date} {b.start_time || '00:00'} → {b.end_date} {b.end_time || '23:59'} IST
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 break-words" data-testid={`banner-message-${b.id}`}>{b.message}</p>
                    <p className="text-xs text-gray-400 mt-1">by {b.created_by_name || 'Admin'}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`banner-edit-${b.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => setToDelete(b)}
                      data-testid={`banner-delete-${b.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="banner-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit banner' : 'Publish new banner'}</DialogTitle>
            <DialogDescription>
              Shown on every employee&apos;s Home page between Start and End dates (inclusive).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="banner-message">Message</Label>
              <Textarea
                id="banner-message"
                value={form.message}
                onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder="e.g. Office will remain closed on Friday for the All-Hands."
                rows={3}
                maxLength={500}
                data-testid="banner-message-input"
              />
              <div className="text-xs text-gray-400 mt-1 text-right">{form.message.length}/500</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="banner-start">Start date</Label>
                <Input
                  id="banner-start" type="date" value={form.start_date}
                  onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                  data-testid="banner-start-input"
                />
              </div>
              <div>
                <Label htmlFor="banner-end">End date</Label>
                <Input
                  id="banner-end" type="date" value={form.end_date}
                  min={form.start_date || undefined}
                  onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
                  data-testid="banner-end-input"
                />
              </div>
              <div>
                <Label htmlFor="banner-start-time">Start time (IST)</Label>
                <Input
                  id="banner-start-time" type="time" value={form.start_time}
                  onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value || '00:00' }))}
                  data-testid="banner-start-time-input"
                />
              </div>
              <div>
                <Label htmlFor="banner-end-time">End time (IST)</Label>
                <Input
                  id="banner-end-time" type="time" value={form.end_time}
                  onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value || '23:59' }))}
                  data-testid="banner-end-time-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-1">
              Times are in <span className="font-medium">IST</span>. Defaults
              are 00:00 → 23:59 (all-day on the chosen dates).
            </p>
            <div>
              <Label htmlFor="banner-severity">Severity</Label>
              <Select
                value={form.severity}
                onValueChange={(value) => setForm(f => ({ ...f, severity: value }))}
              >
                <SelectTrigger id="banner-severity" data-testid="banner-severity-select">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info" data-testid="banner-severity-option-info">
                    Info — amber, routine announcement
                  </SelectItem>
                  <SelectItem value="warning" data-testid="banner-severity-option-warning">
                    Warning — orange/red, needs attention
                  </SelectItem>
                  <SelectItem value="alert" data-testid="banner-severity-option-alert">
                    Alert — red + pulse, urgent
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border rounded-lg px-3 py-2">
              <div>
                <Label className="mb-0">Active</Label>
                <p className="text-xs text-gray-500">Turn off to hide without deleting.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(f => ({ ...f, is_active: checked }))}
                data-testid="banner-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="banner-save-btn" className="bg-amber-600 hover:bg-amber-700">
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Publish banner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="banner-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `"${toDelete.message}" will be permanently removed. This cannot be undone.` : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="banner-delete-confirm">
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default BannerManager;
